import type { Item } from '@/stores/canvasStore';

export interface SpatialCluster {
  id: string;                    // Deterministic ID based on member item IDs
  name: string;                  // Human-readable cluster name from AI
  color: string;                 // Hex color for this cluster
  memberItemIds: string[];       // Items in this cluster
  boundingBox: {
    minX: number; minY: number;
    maxX: number; maxY: number;
    centerX: number; centerY: number;
    width: number; height: number;
  };
  density: number;               // Items per 1000 sq px — tighter = more related
  dominantType: string;          // Most common item_type in the cluster
  itemTypes: Record<string, number>; // Count of each item type
}

/** AI-generated cluster from the edge function */
export interface AICluster {
  name: string;
  itemIds: string[];
  color: string;
}

/**
 * Build SpatialCluster objects from AI-generated cluster assignments.
 * Computes bounding boxes from the actual item positions.
 */
export function buildClustersFromAI(
  aiClusters: AICluster[],
  items: { id: string; item_type: string; position_x: number; position_y: number; width: number; height: number }[]
): SpatialCluster[] {
  const itemMap = new Map(items.map(i => [i.id, i]));

  // Merge clusters named "Other", "Unclustered", "Miscellaneous" into one "Unclustered"
  const MERGE_NAMES = ['other', 'unclustered', 'miscellaneous'];
  const mergeTargets: AICluster[] = [];
  const normalClusters: AICluster[] = [];
  for (const c of aiClusters) {
    if (MERGE_NAMES.includes(c.name.toLowerCase().trim())) {
      mergeTargets.push(c);
    } else {
      normalClusters.push(c);
    }
  }
  if (mergeTargets.length > 0) {
    const mergedIds = mergeTargets.flatMap(c => c.itemIds);
    if (mergedIds.length > 0) {
      normalClusters.push({ name: 'Unclustered', itemIds: mergedIds, color: '#E0E0E0' });
    }
  }
  const resolvedClusters = normalClusters;

  return resolvedClusters.map((cluster) => {
    const members = cluster.itemIds.map(id => itemMap.get(id)).filter(Boolean) as typeof items;
    if (members.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const typeCounts: Record<string, number> = {};

    for (const item of members) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
      typeCounts[item.item_type] = (typeCounts[item.item_type] || 0) + 1;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height || 1;

    return {
      id: cluster.itemIds.sort().join('-').substring(0, 64),
      name: cluster.name,
      color: cluster.color,
      memberItemIds: cluster.itemIds,
      boundingBox: { minX, minY, maxX, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width, height },
      density: members.length / (area / 1000),
      dominantType: Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown',
      itemTypes: typeCounts,
    };
  }).filter(Boolean) as SpatialCluster[];
}

/** 
 * Distance between two items' centers.
 * Uses edge-to-edge distance (not center-to-center) for better grouping.
 */
function itemDistance(a: Item, b: Item): number {
  // Edge-to-edge distance (negative if overlapping)
  const gapX = Math.max(0,
    Math.max(a.position_x, b.position_x) - 
    Math.min(a.position_x + a.width, b.position_x + b.width)
  );
  const gapY = Math.max(0,
    Math.max(a.position_y, b.position_y) - 
    Math.min(a.position_y + a.height, b.position_y + b.height)
  );
  
  return Math.sqrt(gapX * gapX + gapY * gapY);
}

/**
 * Recompute bounding box and stats for a cluster given its member items.
 */
function computeClusterStats(
  memberIds: string[],
  itemMap: Map<string, Item>,
): Pick<SpatialCluster, 'boundingBox' | 'density' | 'dominantType' | 'itemTypes'> | null {
  const members = memberIds.map(id => itemMap.get(id)).filter(Boolean) as Item[];
  if (members.length === 0) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const typeCount: Record<string, number> = {};
  for (const item of members) {
    minX = Math.min(minX, item.position_x);
    minY = Math.min(minY, item.position_y);
    maxX = Math.max(maxX, item.position_x + item.width);
    maxY = Math.max(maxY, item.position_y + item.height);
    typeCount[item.item_type] = (typeCount[item.item_type] || 0) + 1;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  const area = Math.max(width * height, 1);
  return {
    boundingBox: { minX, minY, maxX, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width, height },
    density: (members.length / area) * 1000,
    dominantType: Object.entries(typeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown',
    itemTypes: typeCount,
  };
}

/**
 * Detect spatial clusters of items on the canvas.
 *
 * Algorithm: Distance-based agglomerative clustering
 * - Two items are "neighbors" if their edge-to-edge distance < threshold
 * - Connected components of neighbors form clusters
 * - Single isolated items are NOT clusters (minimum 2 items)
 *
 * @param items - All items on the current board
 * @param proximityThreshold - Max edge-to-edge distance to consider items "neighbors" (default 80px)
 * @param minClusterSize - Minimum items to form a cluster (default 2)
 * @param existingClusters - Optional existing clusters for incremental updates.
 *   When provided, only new items (not in any existing cluster) are evaluated.
 *   They're assigned to the nearest existing cluster within threshold or form new clusters.
 */
export function detectClusters(
  items: Item[],
  proximityThreshold: number = 80,
  minClusterSize: number = 2,
  existingClusters?: SpatialCluster[],
): SpatialCluster[] {
  if (items.length < minClusterSize) return [];

  // Filter out board_link items (they're navigation, not content)
  const contentItems = items.filter(i => i.item_type !== 'board_link');
  if (contentItems.length < minClusterSize) return [];

  const itemMap = new Map(contentItems.map(i => [i.id, i]));

  // --- Incremental path: assign new items to existing clusters ---
  if (existingClusters && existingClusters.length > 0) {
    const existingItemIds = new Set(existingClusters.flatMap(c => c.memberItemIds));
    const newItems = contentItems.filter(i => !existingItemIds.has(i.id));

    if (newItems.length === 0) {
      // No new items — return existing clusters with refreshed bounding boxes
      return existingClusters.map(c => {
        const stats = computeClusterStats(c.memberItemIds, itemMap);
        return stats ? { ...c, ...stats } : c;
      });
    }

    // Clone existing clusters (mutable during assignment)
    const clusters = existingClusters.map(c => ({
      ...c,
      memberItemIds: [...c.memberItemIds],
    }));

    // Try to assign each new item to nearest existing cluster
    const unassigned: Item[] = [];
    for (const newItem of newItems) {
      let bestCluster: (typeof clusters)[number] | null = null;
      let bestDist = Infinity;

      for (const cluster of clusters) {
        for (const memberId of cluster.memberItemIds) {
          const member = itemMap.get(memberId);
          if (!member) continue;
          const dist = itemDistance(newItem, member);
          if (dist <= proximityThreshold && dist < bestDist) {
            bestDist = dist;
            bestCluster = cluster;
          }
        }
      }

      if (bestCluster) {
        bestCluster.memberItemIds.push(newItem.id);
      } else {
        unassigned.push(newItem);
      }
    }

    // Cluster unassigned new items among themselves
    if (unassigned.length >= minClusterSize) {
      const subClusters = detectClusters(
        unassigned as Item[],
        proximityThreshold,
        minClusterSize,
      );
      clusters.push(...subClusters);
    }

    // Recompute stats for every cluster and regenerate IDs
    return clusters.map(c => {
      const sortedIds = [...c.memberItemIds].sort();
      const stats = computeClusterStats(sortedIds, itemMap);
      if (!stats) return null;
      return {
        ...c,
        id: `cluster_${sortedIds.join('_').substring(0, 32)}`,
        memberItemIds: sortedIds,
        ...stats,
      };
    }).filter(Boolean) as SpatialCluster[];
  }

  // --- Full clustering path (no existing clusters) ---

  // Build adjacency list using Union-Find for efficient clustering
  const parent: Map<string, string> = new Map();
  const rank: Map<string, number> = new Map();

  // Initialize each item as its own set
  for (const item of contentItems) {
    parent.set(item.id, item.id);
    rank.set(item.id, 0);
  }

  function find(x: string): string {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)!)); // Path compression
    }
    return parent.get(x)!;
  }

  function union(a: string, b: string): void {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA === rootB) return;

    const rankA = rank.get(rootA)!;
    const rankB = rank.get(rootB)!;
    if (rankA < rankB) {
      parent.set(rootA, rootB);
    } else if (rankA > rankB) {
      parent.set(rootB, rootA);
    } else {
      parent.set(rootB, rootA);
      rank.set(rootA, rankA + 1);
    }
  }

  // Compare all pairs and union neighbors
  for (let i = 0; i < contentItems.length; i++) {
    for (let j = i + 1; j < contentItems.length; j++) {
      const dist = itemDistance(contentItems[i], contentItems[j]);
      if (dist <= proximityThreshold) {
        union(contentItems[i].id, contentItems[j].id);
      }
    }
  }

  // Group items by their cluster root
  const clusterMap = new Map<string, Item[]>();
  for (const item of contentItems) {
    const root = find(item.id);
    if (!clusterMap.has(root)) clusterMap.set(root, []);
    clusterMap.get(root)!.push(item);
  }

  // Convert to SpatialCluster objects, filtering by minClusterSize
  const clusters: SpatialCluster[] = [];

  for (const [, members] of clusterMap) {
    if (members.length < minClusterSize) continue;

    // Calculate bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const typeCount: Record<string, number> = {};

    for (const item of members) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
      typeCount[item.item_type] = (typeCount[item.item_type] || 0) + 1;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const area = Math.max(width * height, 1);
    const density = (members.length / area) * 1000;

    // Determine dominant type
    const dominantType = Object.entries(typeCount)
      .sort((a, b) => b[1] - a[1])[0][0];

    // Generate deterministic ID from sorted member IDs
    const sortedIds = members.map(m => m.id).sort();
    const clusterId = `cluster_${sortedIds.join('_').substring(0, 32)}`;

    clusters.push({
      id: clusterId,
      name: `Cluster ${clusters.length + 1}`, // Fallback name for proximity-based clusters
      color: '#E8D5FF', // Default color
      memberItemIds: sortedIds,
      boundingBox: {
        minX, minY, maxX, maxY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
        width,
        height,
      },
      density,
      dominantType,
      itemTypes: typeCount,
    });
  }

  // Sort by size (largest clusters first)
  clusters.sort((a, b) => b.memberItemIds.length - a.memberItemIds.length);

  return clusters;
}

/**
 * Get a text description of a cluster for AI context.
 */
export function describeCluster(cluster: SpatialCluster, items: Item[]): string {
  const members = items.filter(i => cluster.memberItemIds.includes(i.id));
  const typeList = Object.entries(cluster.itemTypes)
    .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
    .join(', ');
  
  const position = cluster.boundingBox.centerX < 500 && cluster.boundingBox.centerY < 500
    ? 'top-left area'
    : cluster.boundingBox.centerX > 1000
    ? 'right side'
    : cluster.boundingBox.centerY > 800
    ? 'bottom area'
    : 'center area';

  return `Cluster of ${cluster.memberItemIds.length} items (${typeList}) in the ${position}, ` +
    `spanning ${Math.round(cluster.boundingBox.width)}×${Math.round(cluster.boundingBox.height)}px`;
}

/**
 * Find which cluster an item belongs to (if any).
 */
export function getItemCluster(itemId: string, clusters: SpatialCluster[]): SpatialCluster | null {
  return clusters.find(c => c.memberItemIds.includes(itemId)) || null;
}
